import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, LoaderIcon } from 'lucide-react';
interface SampleDealBriefProps {
  onBuyerContactOpen: () => void;
}
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
export function SampleDealBrief({
  onBuyerContactOpen
}: SampleDealBriefProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    const formData = new FormData(e.currentTarget);
    formData.append('access_key', 'f5c0d80c-69cb-435c-bfe1-43daa76cec52');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <main className="bg-deal-black pt-20" data-id="element-754">
      {/* Hero / Main Content Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20" data-id="element-755">
        <div className="absolute inset-0 opacity-[0.025]" style={{
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-756" />
        <div className="absolute inset-0 bg-gradient-to-b from-deal-black/20 via-transparent to-deal-black" data-id="element-757" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full" data-id="element-758">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start" data-id="element-759">
            {/* Left Column: Content */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-760">
              <span className="inline-block text-deal-green text-[11px] font-semibold tracking-[0.25em] uppercase mb-8 border border-deal-green/30 px-4 py-1.5" data-id="element-761">
                For Buyers
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-6" data-id="element-762">
                Sample Deal Brief
              </h1>
              <p className="text-lg text-white/50 leading-relaxed font-light mb-12" data-id="element-763">
                See exactly what you'll receive with each qualified seller
                introduction. This is the format delivered with every deal,
                designed for fast IC-level screening.
              </p>
              {/* Checklist Card */}
              <div className="bg-[#0d0d0d] border border-white/[0.06] p-8 md:p-10 rounded-xl" data-id="element-764">
                <h3 className="text-xl font-bold text-white mb-6" data-id="element-765">
                  What's Inside the Brief
                </h3>
                <ul className="space-y-4" data-id="element-766">
                  {['One-page screening summary with key details at a glance', 'Owner motivation, timeline, and transition posture', 'Revenue and EBITDA ranges with margin context', 'Customer concentration snapshot (top customer %)', 'Key risks, flags, and honest assessment', 'Next-step recommendation (A / B / C grading)', 'Full call notes and recording access'].map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-767">
                      <div className="w-5 h-5 rounded-full bg-deal-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-768">
                        <CheckIcon className="w-3 h-3 text-deal-green" data-id="element-769" />
                      </div>
                      <span className="text-white/70 text-sm leading-relaxed" data-id="element-770">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.div>
            {/* Right Column: Form */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-771">
              <div className="bg-[#111] border border-white/10 p-8 md:p-10 rounded-xl shadow-2xl relative overflow-hidden" data-id="element-772">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-deal-green to-transparent" data-id="element-773" />
                {submitted ? <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="text-center py-12" data-id="element-774">
                    <div className="w-16 h-16 bg-deal-green/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-775">
                      <CheckIcon className="w-8 h-8 text-deal-green" data-id="element-776" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-777">
                      You're In
                    </h3>
                    <p className="text-white/50" data-id="element-778">
                      Check your inbox — the sample deal brief is on its way.
                    </p>
                  </motion.div> : <>
                    <h2 className="text-2xl font-bold text-white mb-2" data-id="element-779">
                      Get the Sample Brief
                    </h2>
                    <p className="text-white/50 text-sm mb-8" data-id="element-780">
                      See the exact format before you commit to anything.
                    </p>
                    <form onSubmit={handleSubmit} className="space-y-5" data-id="element-781">
                      <div data-id="element-782">
                        <label className="block text-xs font-semibold text-white/40 uppercase tracking-wider mb-2" data-id="element-783">
                          Full Name
                        </label>
                        <input type="text" name="name" className="w-full bg-white/[0.03] border border-white/[0.1] rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-deal-green/50 transition-colors" placeholder="John Doe" required data-id="element-784" />
                      </div>
                      <div data-id="element-785">
                        <label className="block text-xs font-semibold text-white/40 uppercase tracking-wider mb-2" data-id="element-786">
                          Email Address
                        </label>
                        <input type="email" name="email" className="w-full bg-white/[0.03] border border-white/[0.1] rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-deal-green/50 transition-colors" placeholder="john@example.com" required data-id="element-787" />
                      </div>
                      <div data-id="element-788">
                        <label className="block text-xs font-semibold text-white/40 uppercase tracking-wider mb-2" data-id="element-789">
                          Message
                        </label>
                        <textarea name="message" rows={3} className="w-full bg-white/[0.03] border border-white/[0.1] rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-deal-green/50 transition-colors resize-none" placeholder="Tell us about your buy box, firm, or any questions..." required data-id="element-790" />
                      </div>
                      {error && <p className="text-red-400 text-sm" data-id="element-791">{error}</p>}
                      <button type="submit" disabled={isSubmitting} className="w-full bg-deal-green text-black font-bold uppercase tracking-widest py-4 rounded-lg hover:bg-deal-green-dark transition-colors mt-2 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-792">
                        {isSubmitting ? <>
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-793" />
                            Sending...
                          </> : <>
                            Get the Sample Deal Brief
                            <ArrowRightIcon className="w-4 h-4" data-id="element-794" />
                          </>}
                      </button>
                      <p className="text-center text-white/30 text-xs mt-4" data-id="element-795">
                        Instant access. No commitment required.
                      </p>
                    </form>
                  </>}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Bottom CTA Band */}
      <section className="py-20 bg-[#0d0d0d] border-t border-white/[0.06]" data-id="element-796">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-797">
          <motion.div {...fadeInUp} data-id="element-798">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-799">
              Ready to go beyond the sample?
            </h2>
            <p className="text-lg text-white/50 mb-10 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-800">
              The sample brief shows you the format. The pilot delivers 30–45 of
              them — each representing a real, qualified business owner in your
              buy box.
            </p>
            <button onClick={onBuyerContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300 inline-flex items-center gap-2" data-id="element-801">
              Start a 90-Day Pilot
              <ArrowRightIcon className="w-4 h-4" data-id="element-802" />
            </button>
          </motion.div>
        </div>
      </section>
    </main>;
}